// spreadsheet utils
import * as xlsx from 'xlsx'

import { isDeepEqual } from 'utils/object'
// import { parseCandidateRow, isValidCandidate } from 'utils/parseCandidateData'

// Hard limits for spreadsheet imports
export const XLSX_MAX_FILES = 10
export const XLSX_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 // 5 MB
export const XLSX_MAX_ROWS_PER_SHEET = 1000

// const xlsx = await import('xlsx')

// Worksheet -> [[*]]
const sheetToMatrix = (sheet: xlsx.WorkSheet): unknown[][] =>
  xlsx.utils.sheet_to_json(sheet, { header: 1 }) as unknown[][]

const correctHeader = ['name', 'email', 'phone']

const pedanticParseSheet = (sheet: xlsx.WorkSheet) => {
  // headers of spreadsheet must be exactly ['name', 'email', 'phone']
  // returns [{name, mail, phone}]
  // convert sheet object to array of arrays
  const [header = [], ...rows] = sheetToMatrix(sheet)
  const headerCells = Array.isArray(header) ? header : []

  if (rows.length > XLSX_MAX_ROWS_PER_SHEET)
    throw new Error(
      `too many rows in spreadsheet (${rows.length}), maximum is ${XLSX_MAX_ROWS_PER_SHEET}`,
    )

  if (isDeepEqual(correctHeader, headerCells.slice(0, 3)))
    return rows
      .map(([name, email, phone]) => ({ name, email, phone }))
      .filter(row => row.email)
  throw new Error(
    `wrong spreadsheet headers: ${headerCells}, expected ${correctHeader}`,
  )
}

// the text after the last dot, or the whole name when there is no dot
const extension = (fileName: string) =>
  fileName.slice(fileName.lastIndexOf('.') + 1)

export const processWorkbooks = (workbooks: xlsx.WorkBook[]) =>
  workbooks
    .flatMap(workbook => Object.values(workbook.Sheets))
    .map(sheet => pedanticParseSheet(sheet))
    .flat()

// File -> Promise[xlsx.workbook]
const readSpreadsheetFile = (file: File): Promise<xlsx.WorkBook> =>
  new Promise((resolve, reject) => {
    if (typeof file.size === 'number' && file.size > XLSX_MAX_FILE_SIZE_BYTES) {
      reject(
        new Error(
          `file "${file.name}" is too large, maximum size is ${
            XLSX_MAX_FILE_SIZE_BYTES / (1024 * 1024)
          }MB`,
        ),
      )
      return
    }

    const reader = new FileReader()
    const decoder = new TextDecoder('utf-8')
    reader.onload = () => {
      try {
        const result = reader.result
        if (!(result instanceof ArrayBuffer)) {
          reject(new Error(`cannot read file "${file.name}"`))
          return
        }

        const byteData = new Uint8Array(result)
        const sheet =
          extension(file.name) === 'csv'
            ? xlsx.read(decoder.decode(byteData), { type: 'string' })
            : xlsx.read(byteData, { type: 'array' })
        resolve(sheet)
      } catch (error) {
        reject(error)
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })

// FileList -> Promise[[{k:v}]]
export const importCandidateSheet = (files: FileList | File[]) => {
  const fileArray = Array.from(files)

  if (fileArray.length > XLSX_MAX_FILES)
    return Promise.reject(
      new Error(`too many files, maximum number of files is ${XLSX_MAX_FILES}`),
    )

  return Promise.all(fileArray.map(readSpreadsheetFile)).then(processWorkbooks)
}

const getMimeType = (filename: string) =>
  ({
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
  })[extension(filename)]

// String -> Promise[File]
export const templateDocument = (filename = 'candidate.template.xlsx') => {
  const row = {}
  for (const key of correctHeader) row[key] = ''
  return exportCandidateSheet([row], filename)
}

// The header comes from the first row and the cells are taken by position, so
// a row with other keys is measured under the columns of the first row
const columnWidths = (rows: Array<Record<string, unknown>>) => {
  const matrix = [
    Object.keys(rows[0] ?? {}),
    ...rows.map(row => Object.values(row)),
  ]
  const columns: unknown[][] = []
  for (const cells of matrix)
    cells.forEach((cell, j) => (columns[j] ??= []).push(cell))
  return columns.map(column => {
    const longest = column
      .filter((cell): cell is string => typeof cell === 'string')
      .reduce((max, cell) => Math.max(max, cell.length), 0)
    return { wch: longest + 2 }
  })
}

export const getFileName = (model: string): string => {
  const date = new Date().toISOString().slice(0, 10)

  return `ebber.${model}.${date}`
}

// [{k:v}] -> Promise[File]
export const exportCandidateSheet = (
  rows: Array<Record<string, unknown>>,
  fileName = 'data.xlsx',
): Promise<File> => {
  const worksheetName = 'data'
  const ext = extension(fileName)
  const worksheet = xlsx.utils.json_to_sheet(rows)
  // set column widths (32 chars)
  worksheet['!cols'] = columnWidths(rows)
  const workbook = xlsx.utils.book_new()
  xlsx.utils.book_append_sheet(workbook, worksheet, worksheetName)
  const writerOptions: xlsx.WritingOptions = {
    type: 'array',
    bookType: ext as xlsx.BookType,
  }
  const arrayBuffer = xlsx.write(workbook, writerOptions)
  return new Promise(resolve => {
    const file = new File([arrayBuffer], fileName, {
      type: getMimeType(fileName),
    })
    resolve(file)
  })
}
